import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { patientApi } from '../../api';
import type { MarketplaceProvider } from '../../types';
import { searchCareServices, getSearchSuggestions } from './careSearch';
import { getCareIntelligence } from './care-intelligence';

// ─── SVG Icons ───────────────────────────────────────────────────────────────

function IconSearch({ color }: { color: string }) {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
    </svg>
  );
}

function IconDoctor({ color }: { color: string }) {
  return (
    <svg width={28} height={28} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
      <circle cx="12" cy="7" r="4" />
      <path d="M12 11v3" /><path d="M10.5 12.5h3" />
    </svg>
  );
}

function IconLab({ color }: { color: string }) {
  return (
    <svg width={28} height={28} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 3h6" /><path d="M10 3v6l-4 9h12l-4-9V3" />
      <circle cx="9" cy="15" r="1" fill={color} stroke="none" />
    </svg>
  );
}

function IconImaging({ color }: { color: string }) {
  return (
    <svg width={28} height={28} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="12" cy="12" r="1.5" fill={color} stroke="none" />
    </svg>
  );
}

function IconPharmacy({ color }: { color: string }) {
  return (
    <svg width={28} height={28} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 3h6l1 4H8L9 3z" />
      <path d="M4 7h16v14H4z" rx="1" />
      <path d="M9 11h6" /><path d="M12 8v10" />
    </svg>
  );
}

function IconHospital({ color }: { color: string }) {
  return (
    <svg width={28} height={28} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 21h18" /><path d="M5 21V7l7-4 7 4v14" />
      <rect x="9" y="10" width="6" height="6" />
      <path d="M12 10v6" /><path d="M9 13h6" />
    </svg>
  );
}

function IconEmergency({ color }: { color: string }) {
  return (
    <svg width={28} height={28} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 17h2" /><path d="M20 17h2" />
      <path d="M3 8l3-5h10l4 5v9H3V8z" />
      <circle cx="7" cy="17" r="2" /><circle cx="17" cy="17" r="2" />
      <path d="M10 6h4" /><path d="M12 4v4" />
    </svg>
  );
}

function IconTelemedicine({ color }: { color: string }) {
  return (
    <svg width={28} height={28} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8" /><path d="M12 17v4" />
      <circle cx="12" cy="10" r="3" />
      <path d="M12 7v0" />
    </svg>
  );
}

function IconInsurance({ color }: { color: string }) {
  return (
    <svg width={28} height={28} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2l8 4v6c0 5-3.5 9.7-8 11-4.5-1.3-8-6-8-11V6l8-4z" />
      <path d="M9 12h6" /><path d="M12 9v6" />
    </svg>
  );
}

function IconChevron({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

function IconShield({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2l8 4v6c0 5-3.5 9.7-8 11-4.5-1.3-8-6-8-11V6l8-4z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

function IconStar({ color }: { color: string }) {
  return (
    <svg width={12} height={12} viewBox="0 0 24 24" fill={color} stroke="none">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );
}

// ─── Service market data ─────────────────────────────────────────────────────

// Category navigation only — no price ranges, coverage percentages or
// response-time SLAs here: those are provider-specific facts that come from
// each provider's real listing, not marketing constants.
interface MarketService {
  icon: React.ReactNode;
  title: string;
  description: string;
  accent: string;
  route: string;
  cta: string;
}

const MARKET_SERVICES: MarketService[] = [
  {
    icon: <IconDoctor color={T.navy} />,
    title: 'Doctors',
    description: 'Consult verified specialists, GPs, and super-specialists near you',
    accent: T.navy,
    route: '/patient/care-services/doctors',
    cta: 'Find Doctors',
  },
  {
    icon: <IconLab color={T.teal} />,
    title: 'Labs & Diagnostics',
    description: 'Blood tests, health packages, home sample collection',
    accent: T.teal,
    route: '/patient/care-services/labs',
    cta: 'Book Test',
  },
  {
    icon: <IconImaging color="#4F46E5" />,
    title: 'Imaging',
    description: 'X-ray, CT scan, MRI, ultrasound',
    accent: '#4F46E5',
    route: '/patient/care-services/imaging',
    cta: 'Book Scan',
  },
  {
    icon: <IconPharmacy color="#059669" />,
    title: 'Pharmacy',
    description: 'Prescription medicines, OTC, refills',
    accent: '#059669',
    route: '/patient/care-services/pharmacy',
    cta: 'Order Medicine',
  },
  {
    icon: <IconHospital color="#DC2626" />,
    title: 'Hospitals',
    description: 'OPD booking, department discovery',
    accent: '#DC2626',
    route: '/patient/care-services/hospitals',
    cta: 'Check Admission',
  },
  {
    icon: <IconEmergency color="#EA580C" />,
    title: 'Emergency',
    description: 'Ambulance dispatch, emergency rooms, SOS alert',
    accent: '#EA580C',
    route: '/patient/care-services/ambulance',
    cta: 'Request Ambulance',
  },
  {
    icon: <IconTelemedicine color="#0891B2" />,
    title: 'Telemedicine',
    description: 'Video consult with verified doctors from home',
    accent: '#0891B2',
    route: '/patient/care-services/telemedicine',
    cta: 'Start Consult',
  },
  {
    icon: <IconInsurance color="#7C3AED" />,
    title: 'Insurance',
    description: 'Check coverage, file claims, compare health plans',
    accent: '#7C3AED',
    route: '/patient/insurance',
    cta: 'Check Coverage',
  },
];

// ─── Recommendation card ─────────────────────────────────────────────────────
// Shows real, top-rated verified providers from the marketplace directory —
// no fabricated distance, price estimate, or "AI-matched" reasoning.

function firstPrice(p: MarketplaceProvider): number | null {
  if (!p.pricing) return null;
  const list = typeof p.pricing === 'string' ? JSON.parse(p.pricing) : p.pricing;
  return Array.isArray(list) && list.length > 0 ? list[0].price : null;
}

function RecommendationCard({ provider, onClick }: { provider: MarketplaceProvider; onClick: () => void }) {
  const [hovered, setHovered] = useState(false);
  const typeColor: Record<string, string> = { DOCTOR: T.navy, LAB: T.teal, PHARMACY: '#059669', HOSPITAL: '#DC2626', IMAGING: '#4F46E5', AMBULANCE: '#EA580C' };
  const color = typeColor[provider.type] || T.teal;
  const typeLabel: Record<string, string> = { DOCTOR: 'Doctor', LAB: 'Lab', PHARMACY: 'Pharmacy', HOSPITAL: 'Hospital', IMAGING: 'Imaging', AMBULANCE: 'Ambulance' };
  const price = firstPrice(provider);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: T.white,
        border: `1px solid ${hovered ? color + '40' : T.border}`,
        borderRadius: 16,
        padding: 16,
        cursor: 'pointer',
        boxShadow: hovered ? T.shadowMd : T.shadow,
        transition: 'all 0.15s ease',
        transform: hovered ? 'translateY(-2px)' : 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        minWidth: 220,
        flex: '1 1 220px',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: T.font, fontSize: 14, fontWeight: 700, color: T.text, lineHeight: 1.3 }}>
            {provider.name}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
            <span style={{
              fontSize: 10, fontWeight: 700, color: T.white, background: color,
              padding: '2px 8px', borderRadius: 6, fontFamily: T.font,
            }}>
              {typeLabel[provider.type] || provider.type}
            </span>
            {provider.rating > 0 && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconStar color="#F59E0B" />
                <span style={{ fontSize: 10, fontWeight: 700, color: T.text, fontFamily: T.font }}>
                  {provider.rating.toFixed(1)}
                </span>
              </span>
            )}
          </div>
        </div>
        <div style={{
          width: 40, height: 40, borderRadius: 12, background: color + '10',
          display: 'grid', placeItems: 'center', flexShrink: 0,
        }}>
          {provider.type === 'DOCTOR' && <IconDoctor color={color} />}
          {provider.type === 'LAB' && <IconLab color={color} />}
          {provider.type === 'PHARMACY' && <IconPharmacy color={color} />}
          {provider.type === 'HOSPITAL' && <IconHospital color={color} />}
        </div>
      </div>

      {/* Info row */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {price !== null && (
          <span style={{
            fontSize: 10, fontWeight: 700, color: '#059669', background: '#F0FDF4',
            padding: '3px 8px', borderRadius: 6, fontFamily: T.font,
          }}>
            From ₹{price}
          </span>
        )}
        {provider.city && (
          <span style={{
            fontSize: 10, fontWeight: 600, color: T.textSec, background: `${T.navy}08`,
            padding: '3px 8px', borderRadius: 6, fontFamily: T.font,
          }}>
            {provider.city}
          </span>
        )}
      </div>

      {provider.isVerified && (
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 4, alignSelf: 'flex-start',
          fontSize: 9, fontWeight: 700, color: '#0A7B4F', background: 'rgba(10,123,79,0.08)',
          padding: '3px 8px', borderRadius: 6,
        }}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#0A7B4F" strokeWidth="2.5"><path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="10" /></svg>
          NeoVerified
        </span>
      )}

      {/* CTA */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: 'auto' }}>
        <span style={{
          fontSize: 11, fontWeight: 700, color: T.teal, fontFamily: T.font,
          display: 'flex', alignItems: 'center', gap: 4,
        }}>
          View options <IconChevron color={T.teal} />
        </span>
      </div>
    </div>
  );
}

// ─── Market service card ─────────────────────────────────────────────────────

function MarketServiceCard({ svc, onClick }: { svc: MarketService; onClick: () => void }) {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        background: hovered ? '#FAFFFE' : T.white,
        border: `1.5px solid ${hovered ? svc.accent + '50' : T.border}`,
        borderRadius: 18,
        padding: 22,
        boxShadow: hovered ? `0 8px 24px ${svc.accent}18` : T.shadow,
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
        transform: hovered ? 'translateY(-3px)' : 'none',
        width: '100%',
        gap: 12,
        minHeight: 260,
      }}
    >
      <div style={{
        width: 56, height: 56, borderRadius: 16, background: `linear-gradient(135deg, ${svc.accent}08, ${svc.accent}14)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        border: `1px solid ${svc.accent}10`,
      }}>
        {svc.icon}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: T.font, fontSize: 15, fontWeight: 800, color: T.text, lineHeight: 1.3, marginBottom: 4 }}>
          {svc.title}
        </div>
        <div style={{
          fontFamily: T.font, fontSize: 11, color: T.textSec, lineHeight: 1.5,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>
          {svc.description}
        </div>
      </div>

      {/* Pricing note — real prices live on each provider's own listing */}
      <div style={{ fontSize: 9, color: T.textTer, fontFamily: T.font, lineHeight: 1.4, width: '100%', marginTop: 'auto' }}>
        Prices and availability shown on each provider&apos;s page
      </div>

      {/* CTA */}
      <div style={{ width: '100%', borderTop: `1px solid ${T.border}`, paddingTop: 8 }}>
        <span style={{
          fontFamily: T.font, fontSize: 12, fontWeight: 700, color: T.white,
          background: svc.accent, borderRadius: 8, padding: '7px 0', width: '100%',
          textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
        }}>
          {svc.cta} <IconChevron color={T.white} />
        </span>
      </div>
    </button>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function CareServicesHub() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [topRecommendations, setTopRecommendations] = useState<MarketplaceProvider[]>([]);

  const searchResults = useMemo(() => searchCareServices(searchQuery), [searchQuery]);
  const intelligence = useMemo(() => getCareIntelligence(), []);

  useEffect(() => {
    let alive = true;
    patientApi.getMarketplaceProviders({ verified: 'true', limit: 3 })
      .then((res) => { if (alive) setTopRecommendations(res.providers ?? []); })
      .catch(() => { if (alive) setTopRecommendations([]); });
    return () => { alive = false; };
  }, []);

  const orderedServices = useMemo(() => {
    if (!intelligence.priorityServices.length) return MARKET_SERVICES;
    const routeMap = new Map(MARKET_SERVICES.map(s => [s.route, s]));
    const ordered: MarketService[] = [];
    for (const route of intelligence.priorityServices) {
      const svc = routeMap.get(route);
      if (svc) ordered.push(svc);
    }
    for (const svc of MARKET_SERVICES) {
      if (!ordered.includes(svc)) ordered.push(svc);
    }
    return ordered;
  }, [intelligence.priorityServices]);

  const handleSearch = () => {
    const q = searchQuery.trim();
    if (!q) return;
    const results = searchCareServices(q);
    if (results.length > 0) {
      navigate(results[0].route);
    } else {
      navigate(`/patient/search?q=${encodeURIComponent(q)}`);
    }
  };

  const providerRoute = (p: MarketplaceProvider) => {
    const routes: Record<string, string> = {
      DOCTOR: '/patient/care-services/doctors',
      LAB: '/patient/care-services/labs',
      PHARMACY: '/patient/care-services/pharmacy',
      HOSPITAL: '/patient/care-services/hospitals',
      IMAGING: '/patient/care-services/imaging',
      AMBULANCE: '/patient/care-services/ambulance',
    };
    return routes[p.type] || '/patient/care-services';
  };

  return (
    <div style={{ fontFamily: T.font, maxWidth: 1100, margin: '0 auto', paddingBottom: 100, background: 'linear-gradient(180deg, #F8FDFD 0%, #F0F9FA 40%, #FAFBFC 100%)', minHeight: '100vh' }}>

      {/* ════════════════════════════════════════════════════════════════════
          MARKET CONTEXT HEADER
      ════════════════════════════════════════════════════════════════════ */}
      <div style={{
        background: `linear-gradient(135deg, ${T.navy} 0%, #003D7A 50%, ${T.teal} 100%)`,
        padding: '44px 24px 64px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Decorative shapes */}
        <div style={{
          position: 'absolute', top: -60, right: -60, width: 220, height: 220,
          borderRadius: '50%', background: 'rgba(255,255,255,0.03)',
        }} />
        <div style={{
          position: 'absolute', bottom: -30, left: -30, width: 140, height: 140,
          borderRadius: '50%', background: 'rgba(0,184,184,0.1)',
        }} />

        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)',
          borderRadius: 20, padding: '4px 14px', marginBottom: 20,
        }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: T.teal }} />
          <span style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.9)', letterSpacing: '0.08em' }}>
            NEOHEALTHCARD MARKETPLACE
          </span>
        </div>

        <h1 style={{
          fontSize: 28, fontWeight: 800, color: T.white, margin: '0 0 10px', lineHeight: 1.2,
        }}>
          Your Care Marketplace
        </h1>
        <p style={{
          fontSize: 14, color: 'rgba(255,255,255,0.8)', margin: 0, lineHeight: 1.6, maxWidth: 480,
        }}>
          Find doctors, labs, pharmacies and hospitals instantly with transparent pricing
        </p>

        {/* Trust badges */}
        <div style={{ display: 'flex', gap: 8, marginTop: 18, flexWrap: 'wrap' }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            background: 'rgba(255,255,255,0.14)', border: '1px solid rgba(255,255,255,0.18)',
            borderRadius: 8, padding: '5px 12px', fontSize: 10, fontWeight: 700,
            color: 'rgba(255,255,255,0.95)', fontFamily: T.font,
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="2.5" strokeLinecap="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" /></svg>
            Book in 2 clicks
          </span>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            background: 'rgba(255,255,255,0.14)', border: '1px solid rgba(255,255,255,0.18)',
            borderRadius: 8, padding: '5px 12px', fontSize: 10, fontWeight: 700,
            color: 'rgba(255,255,255,0.95)', fontFamily: T.font,
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="2.5" strokeLinecap="round"><path d="M12 2l8 4v6c0 5-3.5 9.7-8 11-4.5-1.3-8-6-8-11V6l8-4z" /><path d="M9 12l2 2 4-4" /></svg>
            Transparent pricing guaranteed
          </span>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════
          LAYER 1 — SMART SEARCH BAR
      ════════════════════════════════════════════════════════════════════ */}
      <div style={{ padding: '0 20px', marginTop: -32, position: 'relative', zIndex: 10 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          background: T.white, borderRadius: 16, padding: '14px 18px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)', border: `1px solid ${T.border}`,
        }}>
          <div style={{ flexShrink: 0, opacity: 0.5 }}>
            <IconSearch color={T.navy} />
          </div>
          <input
            type="text"
            placeholder="What are you looking for today? Symptoms, services, doctors, labs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            style={{
              flex: 1, border: 'none', outline: 'none', fontFamily: T.font,
              fontSize: 14, color: T.text, background: 'transparent', padding: 0,
            }}
          />
          {searchQuery.trim().length > 0 && (
            <button
              onClick={handleSearch}
              style={{
                flexShrink: 0, background: T.teal, border: 'none', borderRadius: 10,
                padding: '8px 18px', fontFamily: T.font, fontSize: 13, fontWeight: 700,
                color: T.white, cursor: 'pointer',
              }}
            >
              Search
            </button>
          )}
        </div>

        {/* Search dropdown */}
        {searchQuery.trim().length > 1 && searchResults.length > 0 && (
          <div style={{
            background: T.white, borderRadius: 14, border: `1px solid ${T.border}`,
            boxShadow: T.shadowLg, marginTop: 6, overflow: 'hidden',
          }}>
            {searchResults.slice(0, 6).map((r, i) => (
              <div
                key={i}
                onClick={() => { setSearchQuery(''); navigate(r.route); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
                  cursor: 'pointer', borderBottom: i < Math.min(searchResults.length, 6) - 1 ? `1px solid ${T.border}` : 'none',
                  background: r.urgent ? '#FEF2F2' : 'transparent',
                }}
              >
                <span style={{
                  fontSize: 10, fontWeight: 700, color: T.white,
                  background: r.type === 'doctor' ? T.navy : r.type === 'lab' ? T.teal : r.type === 'pharmacy' ? '#059669' : r.type === 'imaging' ? '#4F46E5' : r.type === 'ambulance' ? '#DC2626' : T.teal,
                  borderRadius: 6, padding: '2px 8px', textTransform: 'capitalize', flexShrink: 0,
                }}>
                  {r.type}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {r.label}
                  </div>
                  {r.cautionMessage && (
                    <div style={{ fontSize: 10, color: '#DC2626', fontWeight: 600, marginTop: 2 }}>{r.cautionMessage}</div>
                  )}
                </div>
                <IconChevron color={T.textTer} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Transparent Pricing Banner ─────────────────────────────────── */}
      <div style={{ padding: '20px 20px 0' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          background: 'linear-gradient(90deg, #F0FDF4, #ECFDF5, #F0FDF4)',
          border: '1px solid #BBF7D0', borderRadius: 14, padding: '12px 18px',
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: 10,
            background: '#DCFCE7', display: 'grid', placeItems: 'center', flexShrink: 0,
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2" strokeLinecap="round">
              <path d="M12 2l8 4v6c0 5-3.5 9.7-8 11-4.5-1.3-8-6-8-11V6l8-4z" /><path d="M9 12l2 2 4-4" />
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#166534', fontFamily: T.font }}>
              Transparent pricing guaranteed
            </div>
            <div style={{ fontSize: 10, color: '#15803D', fontFamily: T.font, marginTop: 2, lineHeight: 1.4 }}>
              All prices shown are confirmed at booking. No hidden fees, no surprises.
            </div>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════
          LAYER 2 — SMART RECOMMENDATION STRIP
      ════════════════════════════════════════════════════════════════════ */}
      {topRecommendations.length > 0 && (
        <div style={{ padding: '28px 20px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 800, color: T.text, margin: 0 }}>
                Top rated on NeoHealthCard
              </h2>
              <p style={{ fontSize: 12, color: T.textSec, margin: '4px 0 0', lineHeight: 1.4 }}>
                Verified, highest-rated providers in the network
              </p>
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 14,
          }}>
            {topRecommendations.map((p) => (
              <RecommendationCard
                key={p.id}
                provider={p}
                onClick={() => navigate(providerRoute(p))}
              />
            ))}
          </div>

          {/* Compare button */}
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
            <button
              onClick={() => navigate('/patient/care-services/doctors')}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: T.white, border: `1.5px solid ${T.teal}`,
                borderRadius: 12, padding: '10px 24px', cursor: 'pointer',
                fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.teal,
                boxShadow: T.shadow, transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = `${T.teal}08`; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = T.white; }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.teal} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
              </svg>
              Compare nearby options
            </button>
          </div>
        </div>
      )}

      {/* ── Intelligence Alerts (if any) ──────────────────────────────── */}
      {intelligence.alerts.length > 0 && (
        <div style={{ padding: '20px 20px 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {intelligence.alerts.slice(0, 3).map((alert, i) => {
            const severityColors = {
              high: { bg: '#FEF2F2', border: '#FECACA', text: '#991B1B' },
              medium: { bg: '#FFFBEB', border: '#FDE68A', text: '#92400E' },
              low: { bg: `${T.info}08`, border: `${T.info}20`, text: '#1E40AF' },
            };
            const c = severityColors[alert.severity];
            return (
              <div
                key={i}
                onClick={() => navigate(alert.route)}
                style={{
                  background: c.bg, border: `1px solid ${c.border}`, borderRadius: 12,
                  padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10,
                  cursor: 'pointer',
                }}
              >
                <div style={{
                  width: 8, height: 8, borderRadius: '50%',
                  background: alert.severity === 'high' ? '#DC2626' : alert.severity === 'medium' ? '#F59E0B' : T.info,
                  flexShrink: 0,
                }} />
                <div style={{ flex: 1, fontFamily: T.font, fontSize: 12, fontWeight: 600, color: c.text }}>
                  {alert.title}
                </div>
                <IconChevron color={c.text} />
              </div>
            );
          })}
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          LAYER 3 — SERVICE MARKET GRID
      ════════════════════════════════════════════════════════════════════ */}
      {/* ── Section Divider — Popular Near You ────────────────────── */}
      <div style={{ padding: '32px 20px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, transparent, ${T.border}, transparent)` }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.teal} strokeWidth="2" strokeLinecap="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" />
            </svg>
            <span style={{ fontSize: 11, fontWeight: 700, color: T.textSec, fontFamily: T.font, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              Browse by Service
            </span>
          </div>
          <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, transparent, ${T.border}, transparent)` }} />
        </div>
      </div>

      <div style={{ padding: '20px 20px 0' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: T.text, margin: 0 }}>
              Browse Services
            </h2>
            <p style={{ fontSize: 12, color: T.textSec, margin: '4px 0 0' }}>
              Providers, prices and availability come from each listing
            </p>
          </div>
        </div>

        <style dangerouslySetInnerHTML={{ __html: `
          @media (min-width: 768px) {
            .nhc-market-grid { grid-template-columns: repeat(3, 1fr) !important; }
          }
          @media (min-width: 1024px) {
            .nhc-market-grid { grid-template-columns: repeat(4, 1fr) !important; }
          }
          .nhc-market-grid > button { height: 100%; }
        `}} />
        <div className="nhc-market-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 16,
        }}>
          {orderedServices.map((svc) => (
            <MarketServiceCard
              key={svc.route}
              svc={svc}
              onClick={() => navigate(svc.route)}
            />
          ))}
        </div>
      </div>

      {/* ── Trust & Compliance Footer ─────────────────────────────────── */}
      <div style={{ padding: '32px 20px 0' }}>
        <div style={{
          background: T.white, border: `1px solid ${T.border}`, borderRadius: 14,
          padding: '16px 20px', display: 'flex', gap: 12, alignItems: 'flex-start',
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: 10, background: `${T.teal}10`,
            display: 'grid', placeItems: 'center', flexShrink: 0,
          }}>
            <IconShield color={T.teal} />
          </div>
          <div>
            <div style={{ fontFamily: T.font, fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 4 }}>
              NeoHealthCard Verified Network
            </div>
            <p style={{ fontFamily: T.font, fontSize: 11, color: T.textTer, margin: 0, lineHeight: 1.6 }}>
              All providers are NeoVerified. Pricing is transparent and confirmed at booking.
              Record sharing uses consent-based audit logs per NeoHealthCard policy.
              Every transaction is audit logged.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
